/**
 * Overflow menu: owner editing, trip navigation, and device actions as a compact sheet.
 */

import { NavLink } from "react-router";
import { Bell, Map, Pencil, RefreshCw, Smartphone, X } from "lucide-react";
import { useEffect, useRef, type RefObject } from "react";

export function MenuSheet({
  role,
  onClose,
  onRefresh,
  trigger,
}: {
  role: string | null;
  onClose: () => void;
  onRefresh: () => void;
  trigger: RefObject<HTMLElement | null>;
}) {
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const root = menuRef.current;
    if (!root) return;
    const triggerEl = trigger.current;
    const focusable = () =>
      [...root.querySelectorAll<HTMLElement>("a, button")].filter((el) => !el.hasAttribute("disabled"));
    focusable()[0]?.focus();

    function onKey(event: KeyboardEvent) {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
        return;
      }
      if (event.key !== "Tab") return;
      const items = focusable();
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      triggerEl?.focus();
    };
  }, [onClose, trigger]);

  return (
    <>
      <button className="sheet-backdrop" type="button" aria-label="Close menu" onClick={onClose} />
      <div className="sheet menu-sheet" ref={menuRef} role="dialog" aria-modal="true" aria-labelledby="menu-title">
        <div className="menu-sheet-head">
          <h2 id="menu-title">Menu</h2>
          <button className="btn-icon" type="button" aria-label="Close" onClick={onClose}>
            <X size={18} />
          </button>
        </div>
        {role === "owner" ? (
          <div className="menu-group">
            <p className="menu-group-label">Editing</p>
            <NavLink className="menu-row" to="/editor" onClick={onClose}>
              <Pencil size={18} aria-hidden="true" />
              <span className="menu-row-copy">
                <strong>Edit trip</strong>
                <span>Plans, bookings and import</span>
              </span>
            </NavLink>
          </div>
        ) : null}
        <div className="menu-group">
          <p className="menu-group-label">Trip</p>
          <NavLink className="menu-row" to="/map" onClick={onClose}>
            <Map size={18} aria-hidden="true" />
            <span className="menu-row-copy">
              <strong>Map</strong>
              <span>Places and directions</span>
            </span>
          </NavLink>
          <NavLink className="menu-row" to="/updates" onClick={onClose}>
            <Bell size={18} aria-hidden="true" />
            <span className="menu-row-copy">
              <strong>Updates</strong>
              <span>Inbox of trip alerts on this phone</span>
            </span>
          </NavLink>
          <NavLink className="menu-row" to="/essentials/phone" onClick={onClose}>
            <Smartphone size={18} aria-hidden="true" />
            <span className="menu-row-copy">
              <strong>This phone</strong>
              <span>Offline pack and sign out</span>
            </span>
          </NavLink>
        </div>
        <div className="menu-group">
          <p className="menu-group-label">This device</p>
          <button
            className="menu-row"
            type="button"
            onClick={() => {
              onClose();
              onRefresh();
            }}
          >
            <RefreshCw size={18} aria-hidden="true" />
            <span className="menu-row-copy">
              <strong>Refresh</strong>
              <span>Check for a newer shared trip</span>
            </span>
          </button>
        </div>
      </div>
    </>
  );
}
